// PRODUCTION ROUTE FOR UNACADEMY ICONS
import { NextPageContextType, PageType } from '@unicons-types/index';
import Anyfeed from '@unicons-comp/Anyfeed/Anyfeed';
import SEO from '@seo/index';
import fetch from '@utils/api-helper/fetch';
import styled from '@emotion/styled';
import { ICONS_ANYFEED } from '@constants/unacademyIcons/iconsEndpoints.constants';
import { HOST_TYPE } from '@utils/api-helper/host';
import IconsLayout from '@layout/UnacademyIconsLayout';
import setupInitialProps from '@utils/setupInitialProps';
import { ANYFEED_PAGINATION_LIMIT } from '@constants/unacademyIcons/anyfeed.constants';
import { getIsLoggedIn } from '@selectors/auth';
import { redirectToRoute } from '@utils/route';
import { fetchTopologyInfo } from '@actions/topologies';
import React, { useEffect } from 'react';
import Container from '@layout/Components/Container';
import useSubscribed from '@hooks/useSubscribed';
import { useSelector } from 'react-redux';
import {
  ICONS_GOAL_UID,
  UA_ICONS_SLUG,
  ICONS_GOAL_META_TITLE,
  ICONS_GOAL_META_DESCRIPTION
} from '@constants/unacademyIcons/icons.constants';
import { getImage } from '@utils/imageHelper';
import Head from 'next/head';
import { readCookie } from '@utils/cookies';
import { useUpdateSelectedGoal } from '@api/hooks/topology/useSelectedGoal';
import useTopology from '@api/hooks/topology/useTopology';
import personSchema from '@seo/schemas/unacademyIcons/personSchema';
import { useRouter } from 'next/router';
import {
  getIsSubscribedToPlus,
  getIsSubscribedToOffline
} from '@selectors/subscription';
import PageReduxProvider from '@cont/App/PageReduxProviderWrapper';

interface IconsProps {
  anyfeed: any;
  paginationLimit: number;
  goalSlug: string;
}

const SEOComp = SEO as any;

const StyledContainer = styled(Container)`` as any;

const Icons: PageType<IconsProps> = ({
  anyfeed,
  paginationLimit,
  goalSlug
}) => {
  const isLoggedIn = useSelector(getIsLoggedIn);
  const isPlusSubscription = useSubscribed(ICONS_GOAL_UID);
  const isOfflineSubscription = useSelector(
    getIsSubscribedToOffline(ICONS_GOAL_UID)
  );

  const router = useRouter();

  const { topology: goalInfo } = useTopology(ICONS_GOAL_UID);

  const setSelectedGoal = useUpdateSelectedGoal();

  useEffect(() => {
    const selectedGoalCookie = readCookie('selectedGoalUid');
    if (!selectedGoalCookie) {
      if (goalInfo?.uid) setSelectedGoal(goalInfo.uid);
    }
    if (goalSlug !== UA_ICONS_SLUG && goalInfo)
      // Slug Correction
      router.replace(
        `/goal/[goalSlug]/${ICONS_GOAL_UID}`,
        `/goal/${goalInfo?.goalSlug || UA_ICONS_SLUG}/${ICONS_GOAL_UID}/`,
        { shallow: true }
      );
    if (isLoggedIn && (isPlusSubscription || isOfflineSubscription)) {
      const redirectionURL = `/goal/${goalSlug}/${ICONS_GOAL_UID}/icons-home`;
      window.location.replace(redirectionURL);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLoggedIn,
    isPlusSubscription,
    goalSlug,
    goalInfo,
    isOfflineSubscription
  ]);

  const currentUrl = `https://unacademy.com/goal/${UA_ICONS_SLUG}/${ICONS_GOAL_UID}`;

  return (
    <>
      <SEOComp
        title={ICONS_GOAL_META_TITLE}
        url={currentUrl}
        schema={personSchema()}
        description={ICONS_GOAL_META_DESCRIPTION}
        openGraph={{
          title: ICONS_GOAL_META_TITLE,
          description: ICONS_GOAL_META_DESCRIPTION,
          images: [
            {
              url: getImage('icons-course-seo.png')
            }
          ],
          url: currentUrl
        }}
      />
      <Head>
        <link rel="shortcut icon" href={getImage('favicon-icon.ico')} />
      </Head>
      <StyledContainer backgroundColor="var(--color-i-all-black)">
        <Anyfeed
          anyfeed={anyfeed}
          count={anyfeed?.count}
          limit={paginationLimit}
          showViewMore
        />
      </StyledContainer>
    </>
  );
};

Icons.displayName = 'Icons Explore';

Icons.pageInfo = {
  name: 'Icons Explore Page',
  isMWebSupported: true,
  layoutOptions: {
    hideIntercom: true,
    hideFreshbot: true
  }
};

Icons.getInitialProps = (setupInitialProps as any)(
  async (ctx: NextPageContextType) => {
    const { store, query, isServer, res } = ctx;
    const { goalSlug } = query;
    let state = store.getState();
    if (!state.topologies.data[ICONS_GOAL_UID]?.selfDataFetched)
      await store.dispatch(fetchTopologyInfo(ICONS_GOAL_UID));
    state = store.getState();

    const isPlusSubscription = getIsSubscribedToPlus(ICONS_GOAL_UID)(state);
    const isOfflineSubscription =
      getIsSubscribedToOffline(ICONS_GOAL_UID)(state);
    const isLoggedIn = getIsLoggedIn(state);

    if (isLoggedIn && (isPlusSubscription || isOfflineSubscription))
      return redirectToRoute(
        isServer,
        res,
        `/goal/${goalSlug}/${ICONS_GOAL_UID}/icons-home`
      );
    try {
      const anyfeedResponse = await fetch(
        `${ICONS_ANYFEED}?offset=0&limit=${ANYFEED_PAGINATION_LIMIT}`,
        {
          getState: store.getState,
          dispatch: store.dispatch,
          useCamelCase: true,
          hasAppClient: true,
          hostType: HOST_TYPE.UNACADEMY_ICONS
        }
      );
      return {
        anyfeed: anyfeedResponse,
        paginationLimit: ANYFEED_PAGINATION_LIMIT,
        goalSlug
      };
    } catch (e) {
      if (e?.response?.status === 401) {
        // TODO: Move to anyfeed fallback generator
        ctx.res.writeHead(302, {
          Location: `/goal/${goalSlug}/${ICONS_GOAL_UID}`
        });
        return ctx.res.end();
      }
    }
  },
  Icons.pageInfo
);

export default Icons;

Icons.Layout = IconsLayout;
// @ts-ignore
Icons.Wrapper = PageReduxProvider;
